import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { useLocation } from "wouter";
import { useMemo } from "react";

export default function FieldGuide() {
  const [, navigate] = useLocation();
  const generatedAt = useMemo(() => new Date().toLocaleString(), []);

  return (
    <div className="field-guide-page container max-w-4xl py-8">
      <style jsx global>{`
        @media print {
          /* Hide app chrome (sidebar/navigation) without affecting the printed article */
          [data-slot='sidebar-gap'],
          [data-slot='sidebar-container'] {
            display: none !important;
          }

          [data-slot='sidebar-inset'] {
            margin: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }

          [data-slot='sidebar-inset'] > main {
            padding: 0 !important;
          }

          body {
            background: #fff !important;
          }

          .field-guide-page {
            max-width: none !important;
            padding: 0 !important;
          }

          .field-guide-print-header {
            display: block !important;
            margin: 0 0 16px 0;
            padding: 0 0 12px 0;
            border-bottom: 1px solid #000;
          }

          .field-guide-page h2,
          .field-guide-page h3 {
            break-after: avoid;
            page-break-after: avoid;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .field-guide-page ul,
          .field-guide-page ol {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      {/* Print-only header */}
      <div className="field-guide-print-header hidden print:block">
        <div className="text-xl font-bold">Water System Field Guide</div>
        <div className="text-sm text-black/80">Generated: {generatedAt}</div>
      </div>

      <div className="mb-6 flex flex-col gap-3 print:hidden">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="px-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>

          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />
            Print / PDF for Inspectors
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold tracking-tight">Water System Field Guide</h1>
          <p className="text-muted-foreground mt-1">
            Audit-focused reference for operators, supervisors, and inspectors. Explains chlorine tracking, configuration locking, and why reports never backfill missing data.
          </p>
        </div>
      </div>

      <div className="prose prose-neutral dark:prose-invert max-w-none prose-h2:mt-10 prose-h3:mt-6 prose-h2:scroll-mt-20">
        <h2>1. Purpose of This System</h2>
        <p>This application records what actually occurred in the water system.</p>
        <p>It does not:</p>
        <ul>
          <li>Estimate missing data</li>
          <li>Backfill historical values</li>
          <li>Recalculate past records after equipment changes</li>
        </ul>
        <p>All reports and calculations are based strictly on:</p>
        <ul>
          <li>Operator-entered daily logs</li>
          <li>The chlorine configuration that was active on that date</li>
          <li>Time-locked historical records</li>
        </ul>
        <div className="not-prose mt-4 rounded-md border bg-muted/40 px-4 py-3">
          <div className="text-sm font-semibold">Why this matters</div>
          <div className="text-sm text-muted-foreground">
            During audits, historical totals must stay stable. That’s why the system never guesses missing values and never recalculates old logs when equipment changes.
          </div>
        </div>

        <h2>2. Daily Logs – How Entries Work</h2>
        <p>Daily logs are the foundation of all reports.</p>
        <p>Each daily log may include:</p>
        <ul>
          <li>Meter readings (start / end)</li>
          <li>Gallons pumped (auto-calculated from meters)</li>
          <li>Hours or GPM</li>
          <li>Chlorine usage</li>
          <li>Chlorine residual measurements</li>
          <li>Operator notes and photos</li>
        </ul>
        <p>Important Notes</p>
        <ul>
          <li>Blank fields mean no data was recorded</li>
          <li>The system will not assume or infer values</li>
          <li>Totals and reports only include submitted logs</li>
          <li>If data is missing, reports will reflect that missing data.</li>
        </ul>

        <h2>3. Chlorine Tracking – Core Concepts</h2>
        <p>Chlorine tracking separates injection from measured residual.</p>
        <p>These are not the same thing.</p>

        <h3>Injection (Usage)</h3>
        <p>Inches Used: How much chlorine solution left the tank</p>
        <p>Gallons Delivered:<br />
          Inches Used × Gallons per Inch
        </p>
        <p>Pounds of Cl₂:<br />
          Gallons Delivered × lbs Cl₂ per Gallon
        </p>
        <p>These values are calculated automatically from the active chlorine configuration.</p>

        <h3>Residual (Field Measurement)</h3>
        <p>Free Chlorine Residual (ppm) is measured in the field</p>
        <p>It is not calculated</p>
        <p>It reflects conditions in the water system, not how much chlorine was injected</p>
        <p>Injection and residual are both required for compliance, but they represent different things.</p>

        <h2>4. Chlorine System Configurations (Very Important)</h2>
        <p>Each well has one or more time-based chlorine configurations.</p>
        <p>A configuration defines:</p>
        <ul>
          <li>Gallons per inch</li>
          <li>Tank capacity and usable height (optional helper fields)</li>
          <li>Chlorine strength (lbs Cl₂ per gallon)</li>
          <li>Effective start date</li>
          <li>Optional end date</li>
        </ul>

        <h3>Why Configurations Lock</h3>
        <p>Once a configuration is active and used by daily logs:</p>
        <ul>
          <li>It becomes read-only</li>
          <li>Past logs must always calculate the same way they did on that date</li>
          <li>This prevents historical data from changing during audits.</li>
        </ul>

        <h3>Equipment Changes (Tank Replacement, Recalibration)</h3>
        <p>If equipment changes:</p>
        <ul>
          <li>Do not edit the old configuration</li>
          <li>Create a new configuration with a new effective date</li>
        </ul>
        <p>The system will:</p>
        <ul>
          <li>Automatically close the previous configuration</li>
          <li>Apply the new configuration only to future logs</li>
          <li>This preserves historical accuracy.</li>
        </ul>

        <h2>5. Tank Sizing &amp; Auto-Calculation</h2>
        <p>Tank sizing fields are optional but recommended.</p>
        <p>When provided:</p>
        <ul>
          <li>Tank Capacity (gallons)</li>
          <li>Usable Height (inches)</li>
        </ul>
        <p>The system can auto-calculate:</p>
        <p>Gallons per Inch = Tank Capacity ÷ Usable Height</p>
        <p>
          The calculated value is stored, not re-computed later, to preserve historical correctness.
        </p>

        <h2>6. Chlorine Strength (lbs Cl₂ per Gallon)</h2>
        <p>This value represents how strong the chlorine solution is.</p>
        <p>Common example:</p>
        <p>12.5% sodium hypochlorite ≈ 0.0125 lbs Cl₂ per gallon</p>
        <p>This allows the system to calculate how many pounds of chlorine were injected when inches are used.</p>
        <p>If unsure, use the preset provided or consult chemical delivery documentation.</p>

        <h2>7. Reports &amp; Supervisor Statistics</h2>
        <p>Reports:</p>
        <ul>
          <li>Sum daily logs</li>
          <li>Respect configuration effective dates</li>
          <li>Highlight missing days or missing residuals</li>
          <li>Never assume missing data</li>
        </ul>
        <p>If a report shows zeros:</p>
        <ul>
          <li>No logs exist for that period, or</li>
          <li>Required data was not entered</li>
        </ul>

        <h2>8. Roles &amp; Responsibilities</h2>
        <h3>Operators</h3>
        <ul>
          <li>Enter daily logs</li>
          <li>Record chlorine usage and residuals</li>
          <li>Upload photos when required</li>
        </ul>

        <h3>Supervisors</h3>
        <ul>
          <li>Manage wells and chlorine configurations</li>
          <li>Review reports and statistics</li>
          <li>Export and retain records</li>
          <li>Maintain regulatory compliance</li>
        </ul>
        <p>Some fields are intentionally restricted to protect data integrity.</p>

        <h2>9. Audit &amp; Compliance Principles</h2>
        <p>This system is designed for:</p>
        <ul>
          <li>Regulatory review</li>
          <li>Historical traceability</li>
          <li>Equipment change documentation</li>
        </ul>
        <p>Core rules:</p>
        <ul>
          <li>No retroactive calculation changes</li>
          <li>No editing historical configurations</li>
          <li>All changes are time-bound and logged</li>
        </ul>
        <p>If something changes in the field, record it going forward, not retroactively.</p>

        <h2>10. What to do if… (Operator Guidance)</h2>

        <h3>…a tank is replaced / resized / recalibrated</h3>
        <ul>
          <li>Create a new chlorine configuration with a new effective (Active From) date.</li>
          <li>Do not edit historical configurations that were used by existing daily logs.</li>
          <li>Add notes describing the field change (tank swap, gauge calibration, etc.).</li>
        </ul>

        <h3>…a past value was entered wrong</h3>
        <ul>
          <li>Do not retroactively change old configurations to “fix” old calculations.</li>
          <li>Proceed forward: enter the correct information on future logs and add notes explaining the correction.</li>
          <li>If a historical log must be corrected, follow your organization’s audit policy and document the reason.</li>
        </ul>

        <h3>…fields appear locked / read-only</h3>
        <ul>
          <li>Locked fields are expected once logs exist: this prevents historical reports from changing.</li>
          <li>To change calculation inputs, create a new configuration with a new effective date.</li>
        </ul>

        <h3>…reports show missing values or zeros</h3>
        <ul>
          <li>This typically means logs are missing for that day/period, or fields were left blank.</li>
          <li>The system does not infer missing data; reports reflect exactly what was entered.</li>
        </ul>

        <h2>11. Summary</h2>
        <ul>
          <li>Daily logs record what happened</li>
          <li>Configurations explain how calculations work</li>
          <li>Reports reflect exactly what was entered</li>
          <li>Historical data never changes</li>
        </ul>
        <p>This ensures accuracy, accountability, and audit safety.</p>
      </div>
    </div>
  );
}
